import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Heart, Bell, User, ArrowLeft } from 'lucide-react';
import Button from '../common/Button';

const Header: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isSearchPage = location.pathname === '/restaurants';
  const showBackButton = !['/', '/dashboard', '/restaurants', '/reservations', '/favorites', '/profile'].includes(location.pathname);
  const isFavoritesPage = location.pathname === '/favorites';
  
  const getPageTitle = () => {
    switch (location.pathname) {
      case '/dashboard':
        return `Welcome back, ${user?.firstName || 'Guest'}!`;
      case '/restaurants':
        return 'Discover Restaurants';
      case '/reservations':
        return 'My Reservations';
      case '/profile':
        return 'My Profile';
      case '/favorites':
        return 'My Favorites';
      default:
        if (location.pathname.startsWith('/restaurants/')) {
          return 'Restaurant Details';
        }
        if (location.pathname.startsWith('/booking/')) {
          return 'Make Reservation';
        }
        return 'RSVD';
    }
  };
  
  return (
    <header className="glassmorphism-nav sticky top-0 z-50 safe-top">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Left side */}
          <div className="flex items-center space-x-3">
            {showBackButton ? (
              <Button
                variant="ghost"
                size="small"
                onClick={() => navigate(-1)}
                leftIcon={<ArrowLeft className="w-5 h-5" />}
                className="p-2"
              />
            ) : (
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-sm">R</span>
                </div>
                <span className="font-bold text-xl text-gray-900 hidden sm:block">RSVD</span>
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {getPageTitle()}
              </h1>
            </div>
          </div>
          
          {/* Right side */}
          <div className="flex items-center space-x-2">
            {!isSearchPage && (
              <Button
                variant="ghost"
                size="small"
                onClick={() => navigate('/restaurants')}
                leftIcon={<Search className="w-5 h-5" />}
                className="p-2 hover:bg-white/50 backdrop-blur-sm rounded-full transition-all duration-300"
              />
            )}

            <Button
              variant="ghost"
              size="small"
              onClick={() => navigate('/favorites')}
              leftIcon={<Heart className={`w-5 h-5 ${isFavoritesPage ? 'fill-red-500 text-red-500' : ''}`} />}
              className="p-2 hover:bg-white/50 backdrop-blur-sm rounded-full transition-all duration-300"
              data-testid="favorites-link"
            />

            <Button
              variant="ghost"
              size="small"
              onClick={() => {/* TODO: Implement notifications */}}
              leftIcon={<Bell className="w-5 h-5" />}
              className="p-2 relative hover:bg-white/50 backdrop-blur-sm rounded-full transition-all duration-300"
            >
              {/* Notification badge */}
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded-full shadow-lg animate-pulse"></div>
            </Button>
            
            <Button
              variant="ghost"
              size="small"
              onClick={() => navigate('/profile')}
              leftIcon={
                user?.profilePhoto ? (
                  <img
                    src={user.profilePhoto}
                    alt="Profile"
                    className="w-6 h-6 rounded-full"
                  />
                ) : (
                  <User className="w-5 h-5" />
                )
              }
              className="p-2 hover:bg-white/50 backdrop-blur-sm rounded-full transition-all duration-300"
              data-testid="user-profile"
            />
          </div>
        </div>
        
        {/* Profile completion prompt */}
        {user && user.profileCompleteness < 70 && (
          <div className="mt-3 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200/50 rounded-lg shadow-sm backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Complete your profile to unlock full features
                </p>
                <p className="text-xs text-yellow-600">
                  {user.profileCompleteness}% complete
                </p>
              </div>
              <Button
                size="small"
                onClick={() => navigate('/profile/setup')}
              >
                Complete
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;